import { CustomPopover, Icon, usePopover } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Popover, Typography } from '@mui/material';
import {
  cloneElement,
  memo,
  MouseEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import * as Styled from './styles';
import type {
  CascadeMenuItem,
  CascadeMenuProps as Props,
  CascadeSubMenuItem,
} from './types';

const SUB_ANCHOR_ORIGIN = {
  vertical: 'top',
  horizontal: 'right',
} as const;
const SUB_TRANSFORM_ORIGIN = {
  vertical: 'top',
  horizontal: 'left',
} as const;

export const CascadeMenu = memo(function CascadeMenu({
  className,
  buttonElement,
  items,
  onSelect,
  state,
  anchorOrigin,
  transformOrigin,
}: Props) {
  const internalPopover = usePopover();
  const popover = state ?? internalPopover;
  const [activeSubKey, setActiveSubKey] = useState<string | null>(null);
  const [subAnchorEl, setSubAnchorEl] = useState<HTMLElement | null>(null);

  const closeAll = useCallback(() => {
    setActiveSubKey(null);
    setSubAnchorEl(null);
    popover.onClose();
  }, [popover]);

  const handleParentEnter = useCallback(
    (item: CascadeMenuItem, e: MouseEvent<HTMLLIElement>) => {
      if (item.disabled) return;
      if (item.subItems?.length) {
        setActiveSubKey(item.key);
        setSubAnchorEl(e.currentTarget);
      } else {
        setActiveSubKey(null);
        setSubAnchorEl(null);
      }
    },
    []
  );

  const handleParentClick = useCallback(
    (item: CascadeMenuItem) => {
      if (item.disabled) return;
      if (!item.subItems?.length) {
        onSelect(item.key);
        closeAll();
      }
    },
    [onSelect, closeAll]
  );

  const handleSubClick = useCallback(
    (sub: CascadeSubMenuItem) => {
      if (sub.disabled) return;
      if (activeSubKey) onSelect(activeSubKey, sub.key);
      closeAll();
    },
    [activeSubKey, onSelect, closeAll]
  );

  const activeItem = useMemo(
    () => items.find((i) => i.key === activeSubKey),
    [items, activeSubKey]
  );

  const trigger = useMemo(
    () =>
      cloneElement(buttonElement, {
        className: classnames(buttonElement.props.className, className),
      }),
    [buttonElement, className]
  );

  return (
    <>
      <CustomPopover
        state={popover}
        element={trigger}
        {...(anchorOrigin ? { anchorOrigin } : {})}
        {...(transformOrigin ? { transformOrigin } : {})}
      >
        {items.map((item) => (
          <Styled.MenuItem
            key={item.key}
            disabled={item.disabled}
            onMouseEnter={(e) => handleParentEnter(item, e)}
            onClick={() => handleParentClick(item)}
          >
            <Styled.ItemTitle>
              {item.icon && (
                <Icon
                  name={item.icon}
                  size={18}
                  color={item.iconColor ?? 'grey.500'}
                />
              )}
              <Typography variant="body4" color="grey.700">
                {item.label}
              </Typography>
            </Styled.ItemTitle>
            {item.subItems?.length ? (
              <Icon name="chevron-right" size={18} color="grey.500" />
            ) : null}
          </Styled.MenuItem>
        ))}
      </CustomPopover>

      <Popover
        open={!!activeItem && !!subAnchorEl}
        anchorEl={subAnchorEl}
        onClose={() => {
          setActiveSubKey(null);
          setSubAnchorEl(null);
        }}
        anchorOrigin={SUB_ANCHOR_ORIGIN}
        transformOrigin={SUB_TRANSFORM_ORIGIN}
        slotProps={{
          paper: {
            onMouseLeave: () => {
              setActiveSubKey(null);
              setSubAnchorEl(null);
            },
          },
        }}
      >
        <Styled.SubmenuPaper>
          {activeItem?.subItems?.map((sub) => (
            <Styled.MenuItem
              key={sub.key}
              disabled={sub.disabled}
              onClick={() => handleSubClick(sub)}
            >
              <Styled.ItemTitle>
                {sub.icon && (
                  <Icon name={sub.icon} size={18} color="grey.500" />
                )}
                <Typography variant="body4" color="grey.700">
                  {sub.label}
                </Typography>
              </Styled.ItemTitle>
            </Styled.MenuItem>
          ))}
        </Styled.SubmenuPaper>
      </Popover>
    </>
  );
});
